import { useTRPC } from "@nwga/features/client";
import { Button } from "@nwga/ui/components/button";
import { Field } from "@nwga/ui/components/field";
import { Input } from "@nwga/ui/components/input";
import { Separator } from "@nwga/ui/components/separator";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { toast } from "sonner";
import { env } from "../../env.ts";
import { authClient, signOut } from "../../lib/auth.ts";
import { HOME_PATH } from "../../navigation.ts";

/** Mirrors Better Auth's `minPasswordLength` in `packages/auth/src/server.ts`. */
const MIN_PASSWORD_LENGTH = 12;

export const Route = createFileRoute("/settings/account")({
	component: AccountSettingsPage,
});

function AccountSettingsPage() {
	const trpc = useTRPC();
	const session = useQuery(trpc.session.queryOptions());
	const user = session.data?.user ?? null;

	if (session.isPending) {
		return <Skeleton className="h-96 w-full" />;
	}

	if (user === null) return null;

	return (
		<div className="space-y-10">
			<div>
				<h1 className="font-semibold text-2xl tracking-tight">Account</h1>
				<p className="mt-1 text-muted-foreground text-sm">
					Your details, your password, and where you're signed in.
				</p>
			</div>

			<ProfileSection
				name={user.name}
				email={user.email}
				emailVerified={user.emailVerified}
			/>
			<Separator />
			<PasswordSection />
			<Separator />
			<SessionsSection />
		</div>
	);
}

function ProfileSection({
	name,
	email,
	emailVerified,
}: {
	readonly name: string;
	readonly email: string;
	readonly emailVerified: boolean;
}) {
	const queryClient = useQueryClient();
	const [value, setValue] = React.useState(name);
	const [pending, setPending] = React.useState(false);
	const [resending, setResending] = React.useState(false);

	const dirty = value.trim() !== name && value.trim().length > 0;

	const save = async (event: React.FormEvent) => {
		event.preventDefault();
		setPending(true);

		const result = await authClient.updateUser({ name: value.trim() });
		setPending(false);

		if (result.error !== null && result.error !== undefined) {
			toast.error(result.error.message ?? "Could not save your name.");
			return;
		}

		await queryClient.invalidateQueries();
		toast.success("Name updated");
	};

	const resendVerification = async () => {
		setResending(true);
		await authClient.sendVerificationEmail({
			email,
			callbackURL: `${env.webUrl}/verify-email`,
		});
		setResending(false);
		toast.success("Sent", {
			description: "Check your inbox for the confirmation link.",
		});
	};

	return (
		<section className="space-y-4">
			<h2 className="font-medium text-lg">Profile</h2>

			<form
				className="max-w-sm space-y-4"
				onSubmit={(event) => void save(event)}
			>
				<Field label="Name">
					{(props) => (
						<Input
							{...props}
							value={value}
							autoComplete="name"
							onChange={(event) => setValue(event.target.value)}
						/>
					)}
				</Field>

				<Field
					label="Email"
					description={
						emailVerified
							? "Confirmed."
							: "Not confirmed yet — some features stay locked until it is."
					}
				>
					{(props) => (
						// Changing an address is a verification flow of its own, not a
						// text edit, so it is deliberately not offered here yet.
						<Input {...props} value={email} readOnly disabled />
					)}
				</Field>

				<div className="flex flex-wrap gap-2">
					<Button type="submit" disabled={!dirty || pending}>
						{pending ? "Saving…" : "Save"}
					</Button>

					{!emailVerified && (
						<Button
							type="button"
							variant="outline"
							disabled={resending}
							onClick={() => void resendVerification()}
						>
							{resending ? "Sending…" : "Resend confirmation"}
						</Button>
					)}
				</div>
			</form>
		</section>
	);
}

function PasswordSection() {
	const [error, setError] = React.useState<string | null>(null);
	const [pending, setPending] = React.useState(false);
	const formRef = React.useRef<HTMLFormElement>(null);

	const submit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setError(null);

		const data = new FormData(event.currentTarget);
		const current = String(data.get("current"));
		const next = String(data.get("next"));
		const confirmation = String(data.get("confirmation"));

		if (next.length < MIN_PASSWORD_LENGTH) {
			setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
			return;
		}

		if (next !== confirmation) {
			setError("Those two passwords do not match.");
			return;
		}

		setPending(true);
		const result = await authClient.changePassword({
			currentPassword: current,
			newPassword: next,
			// Every other session was authenticated with the old password. Somebody
			// changing it because they think it is compromised expects exactly this.
			revokeOtherSessions: true,
		});
		setPending(false);

		if (result.error !== null && result.error !== undefined) {
			setError(result.error.message ?? "That current password was not right.");
			return;
		}

		formRef.current?.reset();
		toast.success("Password changed", {
			description: "Other devices have been signed out.",
		});
	};

	return (
		<section className="space-y-4">
			<h2 className="font-medium text-lg">Password</h2>
			<p className="text-muted-foreground text-sm">
				Changing it signs you out everywhere else.
			</p>

			<form
				ref={formRef}
				className="max-w-sm space-y-4"
				onSubmit={(event) => void submit(event)}
			>
				<Field label="Current password" required>
					{(props) => (
						<Input
							{...props}
							name="current"
							type="password"
							autoComplete="current-password"
							required
						/>
					)}
				</Field>

				<Field
					label="New password"
					required
					description={`At least ${MIN_PASSWORD_LENGTH} characters.`}
				>
					{(props) => (
						<Input
							{...props}
							name="next"
							type="password"
							autoComplete="new-password"
							minLength={MIN_PASSWORD_LENGTH}
							required
						/>
					)}
				</Field>

				<Field
					label="Confirm new password"
					required
					errors={error === null ? undefined : [error]}
				>
					{(props) => (
						<Input
							{...props}
							name="confirmation"
							type="password"
							autoComplete="new-password"
							required
						/>
					)}
				</Field>

				<Button type="submit" disabled={pending}>
					{pending ? "Changing…" : "Change password"}
				</Button>
			</form>
		</section>
	);
}

function SessionsSection() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [pending, setPending] = React.useState(false);

	const signOutEverywhere = async () => {
		setPending(true);
		await authClient.revokeSessions();
		await signOut();
		// Everything cached was fetched as the signed-in user; clearing beats
		// invalidating, so nothing private is readable during a refetch.
		queryClient.clear();
		await navigate({ to: HOME_PATH });
	};

	return (
		<section className="space-y-4">
			<h2 className="font-medium text-lg">Signed-in devices</h2>
			<p className="max-w-prose text-muted-foreground text-sm">
				If you've signed in on a shared or lost device, sign out everywhere.
				You'll need to sign in again here too.
			</p>

			<Button
				variant="outline"
				disabled={pending}
				onClick={() => void signOutEverywhere()}
			>
				{pending ? "Signing out…" : "Sign out everywhere"}
			</Button>
		</section>
	);
}
